"use strict";
/**
 * Activity-type rename & age-adaptive labels.
 *
 * Phase 1 English curriculum rename:
 *   copy_work       → copy_writing
 *   special_words   → common_exception_words
 *   sentence_build  → sentence_building
 *   comprehension   → reading_comprehension
 *   math            → maths
 *   free_write      → independent_writing (canvas) | typed_composition (text)
 *   tracing, letter_formation, dictation, spelling — unchanged
 *
 * `LEGACY_ACTIVITY_TYPE_MAP` exists for one release cycle so in-flight
 * payloads and cached-tablet JSON continue to work while data is migrated.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.ACTIVITY_TYPE_LABELS_BY_STAGE = exports.DEFAULT_MECHANIC_FOR_TYPE = exports.ACTIVITY_TYPE_TO_STRAND = exports.ACTIVITY_TYPE_TO_SUBJECT = exports.LEGACY_ACTIVITY_TYPE_MAP = exports.DEFAULT_ALLOWED_MODES_BY_TYPE = exports.ALLOWED_INPUT_MODES_BY_TYPE = void 0;
exports.resolveFreeWrite = resolveFreeWrite;
exports.toCanonicalActivityType = toCanonicalActivityType;
exports.isActivityType = isActivityType;
exports.getActivityLabel = getActivityLabel;
exports.ALLOWED_INPUT_MODES_BY_TYPE = {
    letter_tracing: ['canvas'],
    letter_formation: ['canvas'],
    copy_writing: ['canvas', 'keyboard', 'speech'],
    dictation: ['canvas', 'keyboard', 'speech'],
    spelling: ['word_tap', 'canvas', 'keyboard', 'speech'],
    sentence_building: ['word_tap', 'canvas', 'keyboard', 'speech'],
    common_exception_words: ['canvas', 'keyboard', 'speech'],
    reading_comprehension: ['canvas', 'keyboard', 'speech'],
    independent_writing: ['canvas', 'keyboard', 'speech'],
    typed_composition: ['keyboard', 'speech', 'canvas'],
    maths: ['numpad', 'canvas', 'speech', 'concrete_manipulative'],
    concrete_maths: ['concrete_manipulative', 'canvas', 'speech'],
    grouping_sorting: ['drag_sort', 'choice_tap', 'canvas'],
    directional_sequence: ['grid_sequence', 'choice_tap', 'canvas'],
    science_observation: ['canvas', 'keyboard', 'speech'],
    history_source: ['canvas', 'keyboard', 'speech'],
    geography_fieldnote: ['canvas', 'keyboard', 'speech'],
    art_design_sketch: ['canvas'],
    computing_design: ['canvas', 'keyboard', 'speech'],
    re_reflection: ['canvas', 'keyboard', 'speech'],
    interactive_story: ['choice_tap'],
    story_reader: ['choice_tap'],
    flashcard_review: ['canvas'],
    quiz: ['choice_tap'],
};
exports.DEFAULT_ALLOWED_MODES_BY_TYPE = {
    letter_tracing: ['canvas'],
    letter_formation: ['canvas'],
    copy_writing: ['canvas', 'keyboard', 'speech'],
    dictation: ['canvas'],
    spelling: ['word_tap', 'canvas'],
    sentence_building: ['word_tap', 'canvas'],
    common_exception_words: ['canvas', 'keyboard', 'speech'],
    reading_comprehension: ['canvas', 'keyboard', 'speech'],
    independent_writing: ['canvas', 'keyboard', 'speech'],
    typed_composition: ['keyboard', 'speech', 'canvas'],
    maths: ['numpad', 'canvas'],
    concrete_maths: ['concrete_manipulative'],
    grouping_sorting: ['drag_sort'],
    directional_sequence: ['grid_sequence'],
    science_observation: ['canvas', 'keyboard', 'speech'],
    history_source: ['canvas', 'keyboard', 'speech'],
    geography_fieldnote: ['canvas', 'keyboard', 'speech'],
    art_design_sketch: ['canvas'],
    computing_design: ['canvas', 'keyboard', 'speech'],
    re_reflection: ['canvas', 'keyboard', 'speech'],
    interactive_story: ['choice_tap'],
    story_reader: ['choice_tap'],
    flashcard_review: ['canvas'],
    quiz: ['choice_tap'],
};
/** Legacy → canonical map. `free_write` resolves via mechanic at ingress. */
exports.LEGACY_ACTIVITY_TYPE_MAP = {
    tracing: 'letter_tracing',
    letter_formation: 'letter_formation',
    copy_work: 'copy_writing',
    dictation: 'dictation',
    spelling: 'spelling',
    sentence_build: 'sentence_building',
    special_words: 'common_exception_words',
    comprehension: 'reading_comprehension',
    // free_write is intentionally omitted — caller must use resolveFreeWrite
    math: 'maths',
};
/**
 * Resolve a legacy `free_write` payload to the correct canonical type based
 * on the mechanic. Canvas-first is the default for ambiguous / missing
 * mechanics per the handwriting-first rule.
 */
function resolveFreeWrite(mechanic) {
    return mechanic === 'text' ? 'typed_composition' : 'independent_writing';
}
/**
 * Map any activity-type string (legacy or canonical) to the canonical type.
 * Throws for truly unknown values to surface typos early.
 */
function toCanonicalActivityType(raw, mechanic) {
    if (isActivityType(raw))
        return raw;
    if (raw === 'free_write')
        return resolveFreeWrite(mechanic);
    const mapped = exports.LEGACY_ACTIVITY_TYPE_MAP[raw];
    if (mapped)
        return mapped;
    throw new Error(`Unknown activity type: ${raw}`);
}
const ACTIVITY_TYPES = [
    'letter_tracing',
    'letter_formation',
    'copy_writing',
    'dictation',
    'spelling',
    'sentence_building',
    'common_exception_words',
    'reading_comprehension',
    'independent_writing',
    'typed_composition',
    'maths',
    'concrete_maths',
    'grouping_sorting',
    'directional_sequence',
    'science_observation',
    'history_source',
    'geography_fieldnote',
    'art_design_sketch',
    'computing_design',
    're_reflection',
    'interactive_story',
    'story_reader',
    'flashcard_review',
    'quiz',
];
function isActivityType(x) {
    return typeof x === 'string' && ACTIVITY_TYPES.includes(x);
}
/** Subject that every activity type belongs to by default. */
exports.ACTIVITY_TYPE_TO_SUBJECT = {
    letter_tracing: 'english',
    letter_formation: 'english',
    copy_writing: 'english',
    dictation: 'english',
    spelling: 'english',
    sentence_building: 'english',
    common_exception_words: 'english',
    reading_comprehension: 'english',
    independent_writing: 'english',
    typed_composition: 'english',
    maths: 'maths',
    concrete_maths: 'maths',
    grouping_sorting: 'science',
    directional_sequence: 'computing',
    science_observation: 'science',
    history_source: 'history',
    geography_fieldnote: 'geography',
    art_design_sketch: 'art_design',
    computing_design: 'computing',
    re_reflection: 're',
    interactive_story: 'pshe',
    story_reader: 'english',
    flashcard_review: 'english',
    quiz: 'english',
};
/** English strand that each English activity type primarily sits within. */
exports.ACTIVITY_TYPE_TO_STRAND = {
    letter_tracing: 'handwriting',
    letter_formation: 'handwriting',
    copy_writing: 'handwriting',
    dictation: 'spelling',
    spelling: 'spelling',
    common_exception_words: 'spelling',
    sentence_building: 'grammar_punctuation',
    reading_comprehension: 'reading',
    independent_writing: 'composition',
    typed_composition: 'composition',
    maths: 'arithmetic',
    concrete_maths: 'number_place_value',
    grouping_sorting: 'working_scientifically',
    directional_sequence: 'computer_science',
    science_observation: 'working_scientifically',
    history_source: 'interpretation',
    geography_fieldnote: 'skills_fieldwork',
    art_design_sketch: 'drawing_painting',
    computing_design: 'computer_science',
    re_reflection: 'expressing_meaning',
    interactive_story: 'health_wellbeing',
    story_reader: 'reading',
    flashcard_review: 'spelling',
};
exports.DEFAULT_MECHANIC_FOR_TYPE = {
    letter_tracing: 'canvas',
    letter_formation: 'canvas',
    copy_writing: 'canvas',
    dictation: 'canvas',
    spelling: 'word_tap',
    sentence_building: 'word_tap',
    common_exception_words: 'canvas',
    reading_comprehension: 'canvas',
    independent_writing: 'canvas',
    typed_composition: 'text',
    maths: 'numpad',
    concrete_maths: 'concrete_manipulative',
    grouping_sorting: 'drag_sort',
    directional_sequence: 'grid_sequence',
    science_observation: 'canvas',
    history_source: 'canvas',
    geography_fieldnote: 'canvas',
    art_design_sketch: 'canvas',
    computing_design: 'canvas',
    re_reflection: 'canvas',
    interactive_story: 'choice_tap',
    story_reader: 'choice_tap',
    flashcard_review: 'canvas',
    quiz: 'choice_tap',
};
/**
 * Default label. Some types (notably `common_exception_words`) show a
 * different label in EYFS contexts — see `getActivityLabel`.
 */
const ACTIVITY_TYPE_LABELS = {
    letter_tracing: 'Letter Tracing',
    letter_formation: 'Letter Formation',
    copy_writing: 'Copy Writing',
    dictation: 'Dictation',
    spelling: 'Spelling',
    sentence_building: 'Sentence Building',
    common_exception_words: 'Common Exception Words',
    reading_comprehension: 'Reading & Comprehension',
    independent_writing: 'Independent Writing',
    typed_composition: 'Typed Composition',
    maths: 'Maths',
    concrete_maths: 'Concrete Maths',
    grouping_sorting: 'Grouping & Sorting',
    directional_sequence: 'Directional Coding',
    science_observation: 'Science Observation',
    history_source: 'History Source Analysis',
    geography_fieldnote: 'Geography Field Notes',
    art_design_sketch: 'Art & Design Sketch',
    computing_design: 'Computing Design',
    re_reflection: 'RE Reflection',
    interactive_story: 'Interactive Story',
    story_reader: 'Story Reader',
    flashcard_review: 'Flashcard Review',
    quiz: 'Quiz',
};
exports.ACTIVITY_TYPE_LABELS_BY_STAGE = {
    concrete_maths: {
        EYFS: 'Counting Fun',
        KS1: 'Hands-on Maths',
        KS2: 'Concrete Maths',
    },
    grouping_sorting: {
        EYFS: 'Sorting Game',
        KS1: 'Group & Sort',
        KS2: 'Categorization',
    },
    directional_sequence: {
        EYFS: 'Robot Steps',
        KS1: 'Code Arrows',
        KS2: 'Algorithm Steps',
    },
    common_exception_words: {
        EYFS: 'Tricky Words',
        KS1: 'Common Exception Words',
        KS2: 'Common Exception Words',
    },
    reading_comprehension: {
        EYFS: 'Listen & Respond',
    },
    independent_writing: {
        EYFS: 'My Own Writing',
        KS1: 'Independent Writing',
        KS2: 'Independent Writing',
    },
    typed_composition: {
        KS2: 'Typed Composition',
    },
    sentence_building: {
        EYFS: 'Build a Sentence',
    },
    science_observation: {
        EYFS: 'Look Write',
        KS1: 'Science Observation',
        KS2: 'Science Observation',
    },
    history_source: {
        EYFS: 'Old Things',
        KS1: 'History Source',
        KS2: 'History Source',
    },
    geography_fieldnote: {
        EYFS: 'Map Fun',
        KS1: 'Geography Notes',
        KS2: 'Geography Notes',
    },
    art_design_sketch: {
        EYFS: 'Draw Make',
        KS1: 'Art Sketch',
        KS2: 'Art Sketch',
    },
    computing_design: {
        EYFS: 'Code Play',
        KS1: 'Computing Design',
        KS2: 'Computing Design',
    },
    re_reflection: {
        EYFS: 'Feel Think',
        KS1: 'RE Reflection',
        KS2: 'RE Reflection',
    },
    interactive_story: {
        EYFS: 'Pick a Story',
        KS1: 'Choose Your Path',
        KS2: 'Interactive Story',
    },
    story_reader: {
        EYFS: 'Listen & Read',
        KS1: 'Story Reader',
        KS2: 'Story Reader',
    },
    flashcard_review: {
        EYFS: 'Card Play',
        KS1: 'Flashcard Review',
        KS2: 'Flashcard Review',
    },
    quiz: {
        EYFS: 'Quick Quiz',
        KS1: 'Quiz',
        KS2: 'Quiz',
    },
};
function getActivityLabel(type, keyStage) {
    if (keyStage) {
        const stageLabel = exports.ACTIVITY_TYPE_LABELS_BY_STAGE[type]?.[keyStage];
        if (stageLabel)
            return stageLabel;
    }
    return ACTIVITY_TYPE_LABELS[type];
}
